import React, { useState, useEffect } from 'react'
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import ThumbDownOutlinedIcon from "@mui/icons-material/ThumbDownOutlined";
import NoteIcon from "@mui/icons-material/Note";
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import ShareIcon from '@mui/icons-material/Share';
import TwitterIcon from '@mui/icons-material/Twitter';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import GitHubIcon from '@mui/icons-material/GitHub';
import LanguageIcon from '@mui/icons-material/Language';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import BusinessIcon from '@mui/icons-material/Business';
import StarIcon from '@mui/icons-material/Star';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonIcon from '@mui/icons-material/Person';
import GoogleIcon from '@mui/icons-material/Google';
import Tooltip from '@mui/material/Tooltip';
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Autocomplete from "@mui/material/Autocomplete";
import Chip from "@mui/material/Chip";
import Snackbar from "@mui/material/Snackbar";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import CloseIcon from '@mui/icons-material/Close';

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Link from "@mui/material/Link";
import { useNavigate, useParams } from 'react-router-dom';
import { getListById, getUserById, removeProfileFromList, shareList, submitProfileFeedback } from '../../services/userService';
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import ProfileView from '../profileDetails/ProfileView';

// Helper function to capitalize first letter of a string
const capitalizeFirstLetter = (str) => {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

// Helper function to get social media icon
const getSocialIcon = (network) => {
  const networkLower = network.toLowerCase();
  switch (networkLower) {
    case 'linkedin':
      return <LinkedInIcon sx={{ fontSize: '25px', color: '#0077b5' }} />;
    case 'twitter':
      return <TwitterIcon sx={{ fontSize: '25px', color: '#1DA1F2' }} />;
    case 'facebook':
      return <FacebookIcon sx={{ fontSize: '25px', color: '#4267B2' }} />;
    case 'instagram':
      return <InstagramIcon sx={{ fontSize: '25px', color: '#E4405F' }} />;
    case 'github':
      return <GitHubIcon sx={{ fontSize: '25px', color: '#333' }} />;
    case 'gravatar':
      return <AccountCircleIcon sx={{ fontSize: '25px', color: '#1e1e1e' }} />;
    case 'angellist':
    case 'angel':
      return <BusinessIcon sx={{ fontSize: '25px', color: '#000000' }} />;
    case 'klout':
      return <StarIcon sx={{ fontSize: '25px', color: '#ff6b35' }} />;
    case 'foursquare':
      return <LocationOnIcon sx={{ fontSize: '25px', color: '#f94877' }} />;
    case 'aboutme':
    case 'about.me':
      return <PersonIcon sx={{ fontSize: '25px', color: '#00405d' }} />;
    case 'google':
    case 'google+':
      return <GoogleIcon sx={{ fontSize: '25px', color: '#4285f4' }} />;
    default:
      return <LanguageIcon sx={{ fontSize: '25px', color: '#666' }} />;
  }
};

// Helper function to get social media URL
const getSocialUrl = (socialProfile) => {
  if (socialProfile.url) {
    // If URL doesn't start with http, add https://
    return socialProfile.url.startsWith('http') 
      ? socialProfile.url 
      : `https://${socialProfile.url}`;
  }
  // Fallback to constructing URL from network and username
  const networkLower = socialProfile.network.toLowerCase();
  const username = socialProfile.username || '';
  switch (networkLower) {
    case 'linkedin':
      return `https://linkedin.com/in/${username}`;
    case 'twitter':
      return `https://twitter.com/${username}`;
    case 'facebook':
      return `https://facebook.com/${username}`;
    case 'instagram':
      return `https://instagram.com/${username}`;
    case 'github':
      return `https://github.com/${username}`;
    case 'gravatar':
      return `https://gravatar.com/${username}`;
    case 'angellist':
    case 'angel':
      return `https://angel.co/${username}`;
    case 'klout':
      return `https://klout.com/${username}`;
    case 'foursquare':
      return `https://foursquare.com/${username}`;
    case 'aboutme':
    case 'about.me':
      return `https://about.me/${username}`;
    case 'google':
    case 'google+':
      return `https://plus.google.com/${username}`;
    default:
      return username ? `https://${username}` : '#';
  }
};

const mergeProfilesWithFeedback = (profiles = [], feedbackList = []) => {
  if (!Array.isArray(profiles) || profiles.length === 0) return [];
  if (!Array.isArray(feedbackList) || feedbackList.length === 0) {
    return profiles.map((profile) => ({
      ...profile,
      feedbackStatus: profile.feedbackStatus || profile.decisionStatus || 'pending',
      feedbackNote: profile.feedbackNote || profile.decisionNote || '',
      feedbackUpdatedAt: profile.feedbackUpdatedAt || profile.decisionUpdatedAt || null
    }));
  }

  return profiles.map((profile) => {
    const feedback = feedbackList.find(
      (item) => item.linkedinUsername === profile.linkedinUsername
    );

    if (!feedback) {
      return {
        ...profile,
        feedbackStatus: profile.feedbackStatus || profile.decisionStatus || 'pending',
        feedbackNote: profile.feedbackNote || profile.decisionNote || '',
        feedbackUpdatedAt: profile.feedbackUpdatedAt || profile.decisionUpdatedAt || null
      };
    }

    return {
      ...profile,
      feedbackStatus: feedback.status || 'pending',
      feedbackNote: feedback.note || '',
      feedbackUpdatedAt: feedback.updatedAt || profile.decisionUpdatedAt || null
    };
  });
};

const SharedListsDetails = () => {
  const { listId } = useParams();
  const navigate = useNavigate();
  const [list, setList] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [emailChips, setEmailChips] = useState([]);
  const [emailInput, setEmailInput] = useState('');
  const [shareLoading, setShareLoading] = useState(false);
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
  const [feedbackProfile, setFeedbackProfile] = useState(null);
  const [feedbackStatus, setFeedbackStatus] = useState('pending');
  const [feedbackNote, setFeedbackNote] = useState('');
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackSnackbar, setFeedbackSnackbar] = useState({
    open: false,
    severity: 'success',
    message: ''
  });
  const [descriptionDialogOpen, setDescriptionDialogOpen] = useState(false);
  const [nuggetsDialogOpen, setNuggetsDialogOpen] = useState(false);

  useEffect(() => {
    const fetchListDetails = async () => {
      try {
        setLoading(true);
        const response = await getListById(listId);
        console.log('List response:', response);
        if (response.status === 'success') {
          setList(response.data);
          const mergedProfiles = mergeProfilesWithFeedback(
            response.data.profiles || [],
            response.data.candidateFeedback || []
          );
          setProfiles(mergedProfiles);
        } else {
          setProfiles([]);
        }
      } catch (error) {
        console.error('Error fetching list details:', error);
        setError('Failed to load list details');
      } finally {
        setLoading(false);
      }
    };

    if (listId) {
      fetchListDetails();
    }
  }, [listId]);

  const handleDeleteProfile = async (linkedinUsername) => {
    try {
      // Optimistically update the UI
      const updatedProfiles = profiles.filter(profile => profile.linkedinUsername !== linkedinUsername);
      setProfiles(updatedProfiles);
      
      // Call API to remove profile from list in backend
      const response = await removeProfileFromList(listId, linkedinUsername);
      if (response.status === 'success') {
      } else {
        // If API call fails, revert the UI change
        setProfiles(profiles);
        console.error('Failed to remove profile from list');
      }
    } catch (error) {
      // If API call fails, revert the UI change
      setProfiles(profiles);
      console.error('Error removing profile:', error);
    }
  };

  // Profile click handler
  const handleProfileClick = (profile) => {
    setSelectedProfile(profile);
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
    setSelectedProfile(null);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedProfile(null);
  };

  const handleShare = () => {
    setShareDialogOpen(true);
  };

  const handleShareDialogClose = () => {
    setShareDialogOpen(false);
    setEmailChips([]);
    setEmailInput('');
  };

  const handleEmailInputChange = (event, newInputValue) => {
    setEmailInput(newInputValue);
  };

  const handleEmailChipsChange = (event, newValue) => {
    setEmailChips(newValue);
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSendShare = async () => {
    // Validate all email chips
    const invalidEmails = emailChips.filter(email => !validateEmail(email));
    
    if (invalidEmails.length > 0) {
      alert('Please enter valid email addresses');
      return;
    }
    
    if (emailChips.length === 0) {
      alert('Please enter at least one email address');
      return;
    }
    
    try {
      setShareLoading(true);
      
      // Call the API to share the list
      const response = await shareList(listId, emailChips);
      
      if (response.status === 'success') {
        alert(`List shared successfully with ${emailChips.length} email(s)!`);
        handleShareDialogClose();
      } else {
        alert('Failed to share list. Please try again.');
      }
    } catch (error) {
      console.error('Error sharing list:', error);
      alert('Failed to share list. Please try again.');
    } finally {
      setShareLoading(false);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && !event.shiftKey && !shareLoading) {
      event.preventDefault();
      // If there's text in the input, add it as a chip
      if (emailInput.trim()) {
        const email = emailInput.trim();
        if (validateEmail(email) && !emailChips.includes(email)) {
          setEmailChips([...emailChips, email]);
          setEmailInput('');
        } else if (!validateEmail(email)) {
          alert('Please enter a valid email address');
        } else {
          alert('This email is already added');
        }
      } else {
        // If no input, try to send share
        handleSendShare();
      }
    }
  };

  const showFeedbackMessage = (severity, message) => {
    setFeedbackSnackbar({
      open: true,
      severity,
      message
    });
  };

  const updateProfileFeedbackState = (linkedinUsername, status, noteValue, updateNote = false) => {
    setProfiles((prevProfiles) =>
      prevProfiles.map((profile) => {
        if (profile.linkedinUsername !== linkedinUsername) {
          return profile;
        }
        return {
          ...profile,
          feedbackStatus: status,
          ...(updateNote ? { feedbackNote: noteValue } : {})
        };
      })
    );
  };

  const handleFeedbackSnackbarClose = () => {
    setFeedbackSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleFeedbackDialogOpen = (profile) => {
    setFeedbackProfile(profile);
    setFeedbackStatus(profile?.feedbackStatus || 'pending');
    setFeedbackNote(profile?.feedbackNote || '');
    setFeedbackDialogOpen(true);
  };

  const resetFeedbackDialogState = () => {
    setFeedbackDialogOpen(false);
    setFeedbackProfile(null);
    setFeedbackStatus('pending');
    setFeedbackNote('');
  };

  const handleFeedbackDialogClose = () => {
    if (feedbackSubmitting) return;
    resetFeedbackDialogState();
  };

  const submitFeedback = async (profile, status, note) => {
    if (!profile?.linkedinUsername) {
      showFeedbackMessage('error', 'LinkedIn username missing for this profile.');
      return;
    }

    const payload = {
      linkedinUsername: profile.linkedinUsername,
      status
    };

    let trimmedNote;
    if (typeof note === 'string') {
      trimmedNote = note.trim();
    }
    const shouldIncludeNote = Boolean(trimmedNote && trimmedNote.length > 0);
    if (shouldIncludeNote) {
      payload.note = trimmedNote;
    }

    try {
      setFeedbackSubmitting(true);
      await submitProfileFeedback(listId, payload);
      showFeedbackMessage('success', `Feedback saved for ${profile.fullName || profile.linkedinUsername}.`);
      updateProfileFeedbackState(
        profile.linkedinUsername,
        status,
        trimmedNote,
        shouldIncludeNote
      );
      resetFeedbackDialogState();
    } catch (error) {
      console.error('Error submitting feedback:', error);
      showFeedbackMessage('error', error.response?.data?.message || 'Failed to submit feedback.');
    } finally {
      setFeedbackSubmitting(false);
    }
  };

  const handleQuickFeedback = (profile, status) => {
    submitFeedback(profile, status, '');
  };

  const handleFeedbackSubmit = () => {
    if (!feedbackProfile) return;
    submitFeedback(feedbackProfile, feedbackStatus, feedbackNote);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!list) {
    return (
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Alert severity="warning">List not found</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column',}}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <IconButton onClick={() => navigate(-1)} sx={{ color: '#666' }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" sx={{ fontWeight: 600, color: '#333' }}>
          {list.name}
        </Typography>
        {/* <IconButton onClick={handleShare} sx={{ color: '#666', ml: 1 }}>
          <ShareIcon />
        </IconButton> */}
      </Box>
      
      {list.description && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body1" sx={{ color: '#666', display: 'inline' }}>
            {(() => {
              const words = list.description.split(' ');
              if (words.length > 20) {
                return words.slice(0, 20).join(' ') + '... ';
              }
              return list.description;
            })()}
          </Typography>
          {list.description.split(' ').length > 20 && (
            <Link
              component="button"
              variant="body1"
              onClick={() => setDescriptionDialogOpen(true)}
              sx={{
                color: '#1976d2',
                textDecoration: 'underline',
                cursor: 'pointer',
                '&:hover': { color: '#1565c0' },
                ml: 0.5
              }}
            >
              Show More
            </Link>
          )}
        </Box>
      )}

      {list.nuggets && String(list.nuggets).trim() && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ color: '#666', mb: 0.5 }}>
            Nuggets
          </Typography>
          <Typography variant="body1" sx={{ color: '#666', display: 'inline' }}>
            {(() => {
              const words = String(list.nuggets).split(' ');
              if (words.length > 20) {
                return words.slice(0, 20).join(' ') + '... ';
              }
              return String(list.nuggets);
            })()}
          </Typography>
          {String(list.nuggets).split(' ').length > 20 && (
            <Link
              component="button"
              variant="body1"
              onClick={() => setNuggetsDialogOpen(true)}
              sx={{
                color: '#1976d2',
                textDecoration: 'underline',
                cursor: 'pointer',
                '&:hover': { color: '#1565c0' },
                ml: 0.5
              }}
            >
              Show More
            </Link>
          )}
        </Box>
      )}
      
      <Typography variant="h6" sx={{ fontWeight: 600, color: '#333', mb: 1 }}>
        Profiles ({profiles.length})
      </Typography>
      
      {profiles.length === 0 ? (
        <Alert severity="info">No profiles found in this list</Alert>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 2 }}>
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 600 }}>Picture & Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Social Footprints</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Organization</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Title</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Current Location</TableCell>
                <TableCell sx={{ fontWeight: 600, minWidth: '300px' }}> Summary</TableCell>
                <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Fitment Score</TableCell>
                <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {profiles.map((profile, index) => {
                const feedbackStatus = profile.feedbackStatus || 'pending';
                const hasNote = Boolean(profile.feedbackNote && profile.feedbackNote.trim().length > 0);
                const isAccepted = feedbackStatus === 'accepted';
                const isRejected = feedbackStatus === 'rejected';
                const fitmentPoints = Array.isArray(profile.fitmentSummary)
                  ? profile.fitmentSummary
                  : profile?.fitmentSummary
                    ? [String(profile.fitmentSummary)]
                    : [];

                const thumbsUpStyles = {
                  color: isAccepted ? '#4caf50' : '#666',
                  backgroundColor: isAccepted ? 'rgba(76, 175, 80, 0.12)' : 'transparent',
                  '&:hover': {
                    color: '#4caf50',
                    backgroundColor: 'rgba(76, 175, 80, 0.2)'
                  }
                };

                const thumbsDownStyles = {
                  color: isRejected ? '#f44336' : '#666',
                  backgroundColor: isRejected ? 'rgba(244, 67, 54, 0.12)' : 'transparent',
                  '&:hover': {
                    color: '#f44336',
                    backgroundColor: 'rgba(244, 67, 54, 0.2)'
                  }
                };

                const noteStyles = {
                  color: hasNote ? '#1976d2' : '#666',
                  backgroundColor: hasNote ? 'rgba(25, 118, 210, 0.12)' : 'transparent',
                  '&:hover': {
                    color: '#1976d2',
                    backgroundColor: 'rgba(25, 118, 210, 0.2)'
                  }
                };

                return (
                <TableRow key={index} sx={{ '&:hover': { backgroundColor: '#fafafa' } }}>
                  {(() => {
                    const status = profile.feedbackStatus || 'pending';
                    const hasNote = Boolean(profile.feedbackNote && profile.feedbackNote.trim().length > 0);
                    const isAccepted = status === 'accepted';
                    const isRejected = status === 'rejected';
                    const thumbsUpStyles = {
                      color: isAccepted ? '#4caf50' : '#666',
                      backgroundColor: isAccepted ? 'rgba(76, 175, 80, 0.12)' : 'transparent',
                      '&:hover': {
                        color: '#4caf50',
                        backgroundColor: 'rgba(76, 175, 80, 0.2)'
                      }
                    };
                    const thumbsDownStyles = {
                      color: isRejected ? '#f44336' : '#666',
                      backgroundColor: isRejected ? 'rgba(244, 67, 54, 0.12)' : 'transparent',
                      '&:hover': {
                        color: '#f44336',
                        backgroundColor: 'rgba(244, 67, 54, 0.2)'
                      }
                    };
                    const noteStyles = {
                      color: hasNote ? '#1976d2' : '#666',
                      backgroundColor: hasNote ? 'rgba(25, 118, 210, 0.12)' : 'transparent',
                      '&:hover': {
                        color: '#1976d2',
                        backgroundColor: 'rgba(25, 118, 210, 0.2)'
                      }
                    };
                    return (
                      <>
                      </>
                    );
                  })()}
                  <TableCell>
                    {profile.error ? (
                      <Typography color="error">Not found</Typography>
                    ) : (
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                        <img src={profile.profilePicture} alt="Profile" style={{ width: '50px', height: '50px', borderRadius: '50%' }} />
                        <Typography 
                          sx={{ 
                            cursor: 'pointer', 
                            color: '#1976d2', 
                            textDecoration: 'underline',
                            fontSize: '0.875rem',
                            textAlign: 'center',
                            '&:hover': { color: '#1565c0' }
                          }}
                          onClick={() => handleProfileClick(profile)}
                        >
                          {profile.fullName || 'N/A'}
                        </Typography>
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>
                    {profile.error ? (
                      <Typography color="error">Not found</Typography>
                    ) : profile.socialProfiles && profile.socialProfiles.length > 0 ? (
                      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, flexWrap: 'wrap' }}>
                        {profile.socialProfiles.map((socialProfile, socialIndex) => (
                          <Tooltip 
                            key={socialIndex} 
                            title={`${socialProfile.network}: ${socialProfile.url || socialProfile.username || ''}`}
                            arrow
                          >
                            <IconButton
                              size="small"
                              onClick={() => {
                                const url = getSocialUrl(socialProfile);
                                if (url && url !== '#') {
                                  window.open(url, '_blank', 'noopener,noreferrer');
                                }
                              }}
                              sx={{ 
                                '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.05)' },
                                padding: '1px'
                              }}
                            >
                              {getSocialIcon(socialProfile.network)}
                            </IconButton>
                          </Tooltip>
                        ))}
                      </Box>
                    ) : profile.linkedinLink ? (
                      <IconButton 
                        size={'large'} 
                        onClick={() => {
                          const url = profile.linkedinLink.startsWith('http') 
                            ? profile.linkedinLink 
                            : `https://${profile.linkedinLink}`;
                          window.open(url, '_blank');
                        }}
                        sx={{ '&:hover': { backgroundColor: 'rgba(0, 119, 181, 0.1)' } }}
                      >
                        <LinkedInIcon sx={{ fontSize: '25px', color: '#0077b5' }} />
                      </IconButton>
                    ) : (
                      <Typography color="text.secondary">No link</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {profile.error ? (
                      <Typography color="error">Not found</Typography>
                    ) : (
                      <Typography variant="body2" sx={{ color: '#666' }}>
                        {profile.company ? capitalizeFirstLetter(profile.company) : 'N/A'}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {profile.error ? (
                      <Typography color="error">Not found</Typography>
                    ) : (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {profile.position && (
                          <Typography variant="body2" sx={{ color: '#666', fontWeight: 500 }}>
                            {capitalizeFirstLetter(profile.position)}
                          </Typography>
                        )}
                        {profile.industry && (
                          <Typography variant="body2" sx={{ color: '#999', fontSize: '0.813rem' }}>
                            Industry: {capitalizeFirstLetter(profile.industry)}
                          </Typography>
                        )}
                        {!profile.position && !profile.industry && (
                          <Typography variant="body2">N/A</Typography>
                        )}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>
                    {profile.error ? (
                      <Typography color="error">Not found</Typography>
                    ) : (
                      profile.location ? capitalizeFirstLetter(profile.location) : 'N/A'
                    )}
                  </TableCell>
                  <TableCell>
                    {profile.error ? (
                      <Typography color="error">Not found</Typography>
                    ) : fitmentPoints.length > 0 ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {fitmentPoints.map((point, idx) => (
                          <Typography 
                            key={idx} 
                            variant="body2" 
                            sx={{ 
                              color: '#666',
                              fontSize: '0.875rem',
                              lineHeight: 1.5,
                              '&::before': {
                                content: '"• "',
                                color: '#1BE885',
                                fontWeight: 'bold'
                              }
                            }}
                          >
                            {point}
                          </Typography>
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">No summary available</Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'center' }}>
                    {profile.error ? (
                      <Typography color="error">Not found</Typography>
                     ) : profile.fitmentScore !== undefined && profile.fitmentScore !== null ? (
                      <>
                       <Chip 
                         label={`${profile.fitmentScore}%`}
                         sx={{ 
                           bgcolor: profile.fitmentScore >= 85 ? '#4caf50' : 
                                    profile.fitmentScore >= 61 ? '#ffeb3b' : 
                                    profile.fitmentScore >= 41 ? '#ff9800' : 
                                    '#f44336',
                           color: profile.fitmentScore >= 61 ? '#000' : '#fff',
                           fontWeight: 600,
                           fontSize: '0.875rem',
                           minWidth: '60px'
                         }}
                       /> 
                       {/* <Typography variant="body2" color="text.secondary">
                        <TipsAndUpdatesIcon />
                       </Typography> */}
                       </>
                    ) : (
                      <Typography variant="body2" color="text.secondary">N/A</Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'center' }}>
                    {profile.error ? (
                      <Typography color="error">Not found</Typography>
                    ) : (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center' }}>
                        <Tooltip title="Thumbs Up">
                          <IconButton
                            size="small"
                            disabled={feedbackSubmitting}
                            onClick={() => handleQuickFeedback(profile, 'accepted')}
                            sx={{
                              ...thumbsUpStyles,
                              transition: 'background-color 0.2s ease, color 0.2s ease',
                              size: '24px'
                            }}
                          >
                            <ThumbUpOutlinedIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Thumbs Down">
                          <IconButton
                            size="small"
                            disabled={feedbackSubmitting}
                            onClick={() => handleQuickFeedback(profile, 'rejected')}
                            sx={{
                              ...thumbsDownStyles,
                              transition: 'background-color 0.2s ease, color 0.2s ease'
                            }}
                          >
                            <ThumbDownOutlinedIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={hasNote ? 'View / edit note' : 'Add note'}>
                          <IconButton
                            size="small"
                            disabled={feedbackSubmitting}
                            onClick={() => handleFeedbackDialogOpen(profile)}
                            sx={{
                              ...noteStyles,
                              transition: 'background-color 0.2s ease, color 0.2s ease'
                            }}
                          >
                            <NoteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Profile Detail Dialog */}
      <Dialog open={dialogOpen} onClose={handleDialogClose} fullWidth maxWidth={false} PaperProps={{sx: { width: '60vw', height: '80vh', marginTop: '64px', marginLeft: '240px', marginRight: '240px', borderRadius: 2, }}}>
        <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={(theme) => ({
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          })}
        >
          <CloseIcon />
        </IconButton>
        <Box sx={{ width: '100%', height: '100%', p: 2 }}>
          {selectedProfile && (
            <ProfileView profileData={selectedProfile} />
          )}
        </Box>
      </Dialog>

      {/* Feedback Dialog */}
      <Dialog open={feedbackDialogOpen} onClose={handleFeedbackDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>Log Candidate Feedback</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {feedbackProfile?.fullName || feedbackProfile?.linkedinUsername || 'Candidate'}
          </Typography>
          <FormControl fullWidth>
            <InputLabel id="feedback-status-label">Status</InputLabel>
            <Select
              labelId="feedback-status-label"
              label="Status"
              value={feedbackStatus}
              onChange={(event) => setFeedbackStatus(event.target.value)}
            >
              <MenuItem value="accepted">Accepted</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Notes (optional)"
            multiline
            minRows={3}
            value={feedbackNote}
            onChange={(event) => setFeedbackNote(event.target.value)}
            placeholder="Add details that will help other reviewers..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleFeedbackDialogClose} disabled={feedbackSubmitting}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleFeedbackSubmit}
            disabled={feedbackSubmitting}
            sx={{ bgcolor: '#1BE885', '&:hover': { bgcolor: '#17d077' } }}
          >
            {feedbackSubmitting ? <CircularProgress size={20} color="inherit" /> : 'Save Feedback'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Share Dialog */}
      <Dialog open={shareDialogOpen} onClose={handleShareDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Share List
          <IconButton
            aria-label="close"
            onClick={handleShareDialogClose}
            sx={(theme) => ({
              color: theme.palette.grey[500],
            })}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
            Share "{list.name}" with others by entering their email addresses below.
          </Typography>
          <Autocomplete
            multiple
            freeSolo
            options={[]}
            value={emailChips}
            onChange={handleEmailChipsChange}
            inputValue={emailInput}
            onInputChange={handleEmailInputChange}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  variant="outlined"
                  label={option}
                  {...getTagProps({ index })}
                  key={option}
                  onDelete={() => {
                    const newChips = emailChips.filter((_, i) => i !== index);
                    setEmailChips(newChips);
                  }}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                autoFocus
                margin="dense"
                label="Email Addresses"
                placeholder="Enter email address and press Enter"
                variant="outlined"
                onKeyPress={handleKeyPress}
                helperText="Enter email addresses and press Enter to add. Press Enter again to send."
                sx={{ mt: 1 }}
              />
            )}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleShareDialogClose} color="secondary" disabled={shareLoading}>
            Cancel
          </Button>
          <Button 
            onClick={handleSendShare} 
            variant="contained" 
            color="primary"
            disabled={shareLoading || emailChips.length === 0}
          >
            {shareLoading ? 'Sending...' : 'Send Share'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={feedbackSnackbar.open}
        autoHideDuration={4000}
        onClose={handleFeedbackSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleFeedbackSnackbarClose} 
          severity={feedbackSnackbar.severity} 
          sx={{ width: '100%' }}
        >
          {feedbackSnackbar.message}
        </Alert>
      </Snackbar>

      {/* Description Dialog */}
      <Dialog 
        open={descriptionDialogOpen} 
        onClose={() => setDescriptionDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          List Description
          <IconButton
            aria-label="close"
            onClick={() => setDescriptionDialogOpen(false)}
            sx={(theme) => ({
              color: theme.palette.grey[500],
            })}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ color: '#666', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
            {list?.description || 'No description available'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDescriptionDialogOpen(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Nuggets Dialog */}
      <Dialog
        open={nuggetsDialogOpen}
        onClose={() => setNuggetsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Nuggets
          <IconButton
            aria-label="close"
            onClick={() => setNuggetsDialogOpen(false)}
            sx={(theme) => ({
              color: theme.palette.grey[500],
            })}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ color: '#666', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
            {list?.nuggets || '—'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNuggetsDialogOpen(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default SharedListsDetails
